/**
 * Logging Utility
 *
 * Centralized logging for console and Datadog integration.
 * Supports different log levels and PII masking for production.
 *
 * Key Features:
 * - Filters out backend errors (only logs client-side errors)
 * - Automatically adds traceability context (eventNumber, contractNumber, currentScreen,
 *   sessionId, parentClientId)
 * - Removes all confidential data from logs
 * - Sends relevant logs to DataDog with proper filtering
 */

import { getLoggingContext } from './loggingContext';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  claimId?: string;
  screenCode?: string;
  userId?: string;
  action?: string;
  eventNumber?: string | null;
  contractNumber?: string | null;
  currentScreen?: string | null;
  sessionId?: string;
  /**
   * Parent container's id for the connected user (DEV-7699). Always set by the logger
   * from the parent context — callers do not pass it. Pairs with sessionId to attribute
   * an action: "which visit" + "which user".
   */
  parentClientId?: string | null;
  errorType?: string;
  errorMessage?: string;
  errorCode?: string;
  httpStatus?: number;
  [key: string]: any;
}

/** Truncation limit for error messages sent to Datadog */
const MAX_LOGGED_MESSAGE_LENGTH = 200;

/** Minimal surface of the Datadog Browser Logs SDK used by this logger */
interface DatadogLogs {
  logger: Record<LogLevel, (message: string, context?: object) => void>;
}

/**
 * The Datadog SDK instance published on window by setupDataDog() once
 * initialization succeeded (token configured). Its presence is the contract
 * for "logs are forwarded to Datadog" — also the seam tests use to stub it.
 */
function getDatadog(): DatadogLogs | undefined {
  // typeof guard: keeps the logger importable in windowless environments (node-run tests)
  return typeof window === 'undefined'
    ? undefined
    : (window as { DD_LOGS?: DatadogLogs }).DD_LOGS;
}

/**
 * Logger class for structured logging
 */
class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  /**
   * Log debug message (only in development) with traceability context
   * Note: This method is no-op in production to prevent debug logs from cluttering production.
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      const loggingContext = getLoggingContext();
      const enrichedContext: LogContext = {
        ...context,
        eventNumber: loggingContext.eventNumber,
        contractNumber: loggingContext.contractNumber,
        currentScreen: loggingContext.currentScreen,
        sessionId: loggingContext.sessionId,
        parentClientId: loggingContext.parentClientId,
      };
      this.log('debug', message, enrichedContext);
    }
    // No-op in production
  }

  /**
   * Log info message with traceability context
   */
  info(message: string, context?: LogContext): void {
    const loggingContext = getLoggingContext();
    const enrichedContext: LogContext = {
      ...context,
      eventNumber: loggingContext.eventNumber,
      contractNumber: loggingContext.contractNumber,
      currentScreen: loggingContext.currentScreen,
      sessionId: loggingContext.sessionId,
      parentClientId: loggingContext.parentClientId,
    };
    this.log('info', message, enrichedContext);
  }

  /**
   * Log warning message with traceability context
   */
  warn(message: string, context?: LogContext): void {
    const loggingContext = getLoggingContext();
    const enrichedContext: LogContext = {
      ...context,
      eventNumber: loggingContext.eventNumber,
      contractNumber: loggingContext.contractNumber,
      currentScreen: loggingContext.currentScreen,
      sessionId: loggingContext.sessionId,
      parentClientId: loggingContext.parentClientId,
    };
    this.log('warn', message, enrichedContext);
  }

  /**
   * Log error message with client-side error filtering and traceability context.
   * Only logs client-side errors to DataDog (filters out backend errors).
   *
   * @param message - Human-readable error message
   * @param context - Optional context object
   * @param error - Optional error object
   */
  error(message: string, context?: LogContext, error?: Error | any): void {
    // Filter out backend errors - only log client-side errors
    if (error && !this.isClientSideError(error)) {
      // Backend error - skip logging to DataDog
      if (!getDatadog()) {
        // Datadog not initialized (no token): console is local-only, the full
        // error object is safe and useful for debugging
        console.error(`[BACKEND ERROR - NOT LOGGED] ${message}`, context, error);
      } else {
        // Datadog is initialized — forwardErrorsToLogs ships console.error there
        // whenever a client token is configured (including local dev), so never
        // pass the raw error object (its axios config can carry the Authorization
        // JWT). Log only a sanitized one-line summary.
        console.error(
          `[BACKEND ERROR - SUMMARY] ${message} ` +
          `(status=${error.response?.status ?? error.status ?? 'n/a'}, code=${error.code ?? 'n/a'}, type=${error.name ?? 'Error'}` +
          `${context?.action ? `, action=${context.action}` : ''})`
        );
      }
      return;
    }

    // Get traceability context
    const loggingContext = getLoggingContext();

    // Build safe context (NO confidential data)
    const safeContext: LogContext = {
      ...context,

      // Traceability
      eventNumber: loggingContext.eventNumber,
      contractNumber: loggingContext.contractNumber,
      currentScreen: loggingContext.currentScreen,
      sessionId: loggingContext.sessionId,
      parentClientId: loggingContext.parentClientId,

      // Error info (sanitized)
      ...(error && {
        errorType: error.name || 'Error',
        errorMessage: this.extractErrorMessage(error),
        // Raw axios errors carry the status on response; transformed errors
        // from the axios interceptor carry it at the top level
        httpStatus: error.response?.status ?? error.status,
        errorCode: error.code,
      }),
    };

    // Remove confidential fields explicitly.
    // `clientId` is dropped as a CALLER-supplied field: the identifier is published
    // under exactly one canonical name, `parentClientId`, set from the parent context
    // in the traceability block above (DEV-7699). Dropping it here cannot remove that
    // one — the keys differ — and it keeps an ad-hoc `clientId` out of Datadog.
    delete safeContext.claimId;
    delete safeContext.userId;
    delete safeContext.clientId;
    delete safeContext.jwt;
    delete safeContext.token;
    delete safeContext.requestBody;
    delete safeContext.responseBody;
    delete safeContext.stepData;
    delete safeContext.parentContext;

    // Console logging
    this.log('error', message, safeContext);

    // Send to Datadog in production
    const datadog = getDatadog();
    if (!this.isDevelopment && datadog) {
      try {
        const sanitized = sanitizePII(safeContext);
        datadog.logger.error(message, sanitized);
      } catch (err) {
        console.error('[Logger] Failed to send error to DataDog:', err);
      }
    }
  }

  /**
   * Extract a loggable, truncated message from the error shapes this logger
   * receives:
   * - raw axios / JS errors — string message
   * - backend validation payloads — array of messages (first one wins)
   * - transformed errors from the axios interceptor — bilingual {fr, en}
   *   templated UI copy interpolating only non-user values (retry delay,
   *   WAF tracking ID), no PII; English kept for log consistency
   */
  private extractErrorMessage(error: { message?: unknown }): string {
    const message = error.message;
    let resolved: string | undefined;

    if (typeof message === 'string') {
      resolved = message;
    } else if (Array.isArray(message) && typeof message[0] === 'string') {
      resolved = message[0];
    } else {
      const english = (message as { en?: unknown } | null | undefined)?.en;
      if (typeof english === 'string') {
        resolved = english;
      }
    }

    return resolved
      ? resolved.substring(0, MAX_LOGGED_MESSAGE_LENGTH)
      : 'Unknown error';
  }

  /**
   * Determines if an error is a client-side error (should be logged)
   * vs a backend error (should NOT be logged).
   *
   * @param error - Error object to classify
   * @returns true if client-side error, false if backend error
   */
  private isClientSideError(error: any): boolean {
    // JavaScript runtime exceptions (always client-side)
    if (error instanceof TypeError) return true;
    if (error instanceof ReferenceError) return true;
    if (error instanceof SyntaxError) return true;
    if (error instanceof RangeError) return true;

    // Network errors (client-side)
    if (error.code === 'ERR_NETWORK') return true;
    if (error.code === 'ECONNABORTED') return true;

    // Handle error.message as string or array
    const messageStr = typeof error.message === 'string'
      ? error.message
      : Array.isArray(error.message)
        ? error.message[0] || ''
        : '';
    const messageLower = messageStr.toLowerCase();

    if (messageLower.includes('network error')) return true;
    if (messageLower.includes('failed to fetch')) return true;

    // CORS errors (client-side)
    if (messageLower.includes('cors')) return true;

    // WAF blocks (403 with HTML response) - client-side error
    if (error.response?.status === 403) {
      const contentType = error.response.headers?.['content-type'] || '';
      if (contentType.includes('text/html')) {
        return true; // WAF block - client-side error
      }
    }

    // Backend errors (JSON responses) - DO NOT LOG
    const backendStatusCodes = [400, 401, 403, 404, 409, 422, 500, 502, 503, 504];
    if (error.response?.status && backendStatusCodes.includes(error.response.status)) {
      const contentType = error.response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        return false; // Backend error - skip logging
      }
    }

    // Default: log unknown errors (better safe than sorry)
    return true;
  }

  /**
   * Internal log method
   */
  private log(level: LogLevel, message: string, context?: LogContext): void {
    const timestamp = new Date().toISOString();
    const formattedContext = context
      ? JSON.stringify(context, null, 2)
      : '';

    const logMessage = `[${level.toUpperCase()}] [${timestamp}] ${message}${
      formattedContext ? '\n' + formattedContext : ''
    }`;

    // Console logging
    switch (level) {
      case 'debug':
        console.debug(logMessage);
        break;
      case 'info':
        console.info(logMessage);
        break;
      case 'warn':
        console.warn(logMessage);
        break;
      case 'error':
        console.error(logMessage);
        break;
    }

    // Production logging (Datadog)
    // Send info, warn, and error logs to DataDog in production
    if (!this.isDevelopment && (level === 'info' || level === 'warn' || level === 'error')) {
      this.sendToDatadog(level, message, context);
    }
  }

  /**
   * Send logs to Datadog
   */
  private sendToDatadog(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    try {
      const datadog = getDatadog();
      if (!datadog) {
        console.warn('[Logger] DataDog logs not available - skipping DataDog logging');
        return;
      }

      const sanitized = sanitizePII(context);
      datadog.logger[level](message, sanitized);
    } catch (err) {
      console.error(`[Logger] Failed to send ${level} to DataDog:`, err);
    }
  }
}

/**
 * Mask PII data before logging
 */
export function sanitizePII(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sanitized = { ...data };

  // List of fields that might contain PII
  const piiFields = [
    'healthCard',
    'healthCardNumber',
    'socialInsuranceNumber',
    'sin',
    'bankAccount',
    'accountNumber',
    'routingNumber',
    'creditCard',
    'cardNumber',
    'cvv',
    'password',
    'token',
    'jwt',
    'phoneNumber',
    'email',
    'dateOfBirth',
    'birthDate',
  ];

  // Recursively sanitize nested objects
  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();

    // Check if key contains PII
    const isPII = piiFields.some((field) =>
      lowerKey.includes(field.toLowerCase())
    );

    if (isPII) {
      // Mask the value
      if (typeof sanitized[key] === 'string') {
        sanitized[key] = '***REDACTED***';
      } else {
        sanitized[key] = '[PII]';
      }
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizePII(sanitized[key]);
    }
  });

  return sanitized;
}

// Export singleton instance
export const logger = new Logger();

// Export default
export default logger;
